"use client";
import React, { useState } from "react";
import FilterSection from "./FilterSection";
import PriceRangeSection from "./PriceRangeSection";
import { getValidTab } from "@/utils/getValidTabs";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { PackageViewType, PackageViewTypeTabs } from "./PackageViewType";
import ListView from "./ListView";
import GridView from "./GridView";
import Title from "@/core/common/Title";
import { IPackageRecord } from "../interface/IPackageData.interface";
import Pagination from "./Pagination";

interface IProps {
  packageData: IPackageRecord[];
  tripTypes: string[];
  totalRecords: number;
  currentPage: number;
  perPage: number;
}

const OurTourPackages: React.FC<IProps> = ({
  packageData,
  tripTypes,
  totalRecords,
  currentPage,
  perPage,
}) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const searchParamsObj = Object.fromEntries(searchParams.entries());

  const durations = packageData.map((p) => p.duration);
  const uniqueDurations = Array.from(new Set(durations));

  const [filterOpen, setFilterOpen] = useState(false);

  const goToPage = (page: number) => {
    router.push(
      `${pathname}?${new URLSearchParams({
        ...searchParamsObj,
        p: page.toString(),
      })}`,
      { scroll: false }
    );
  };

  const changePerPage = (value: number) => {
    router.push(
      `${pathname}?${new URLSearchParams({
        ...searchParamsObj,
        page_size: value.toString(),
        p: "1",
      })}`,
      { scroll: false }
    );
  };

  const activeTabLabel = PackageViewTypeTabs?.find((item) => item.id)?.label;

  const viewTypeParams = searchParams.get("view");
  const viewType = getValidTab(
    viewTypeParams,
    PackageViewTypeTabs,
    PackageViewTypeTabs[0]?.id
  );

  const viewTypeContent: Record<PackageViewType, React.JSX.Element> = {
    list: <ListView activeTab={activeTabLabel || ""} content={packageData} />,
    grid: <GridView activeTab={activeTabLabel || ""} content={packageData} />,
  };

  return (
    <div className="padding-x relative">
      <Title
        label="Ready to Explore?"
        primaryText="Our"
        highlightText="Tour Packages"
      />

      <div className="flex gap-5 mt-10 items-start">
        <div className="md:w-full">
          <FilterSection totalItems={packageData?.length} />
          <hr className="my-5" />
          <div className="hidden md:block flex-shrink-0 my-5">
            <PriceRangeSection
              tripTypes={tripTypes}
              duration={uniqueDurations}
            />
          </div>
          <hr className="my-5" />
          {viewTypeContent[viewType]}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalRecords={totalRecords}
        perPage={perPage}
        goToPage={goToPage}
        changePerPage={changePerPage}
      />

      <button
        onClick={() => setFilterOpen(true)}
        className="fixed bottom-4 right-4 z-40 md:hidden bg-yellow-500 text-white px-6 py-3 rounded-full shadow-lg"
      >
        Filters
      </button>

      {filterOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setFilterOpen(false)}
        />
      )}

      <div
        className={`fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl transition-transform md:hidden ${
          filterOpen ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ maxHeight: "85vh" }}
      >
        <div className="p-4 border-b flex justify-between">
          <h3 className="font-medium">Filters</h3>
          <button onClick={() => setFilterOpen(false)}>✕</button>
        </div>

        <div className="p-4 overflow-y-auto">
          <PriceRangeSection tripTypes={tripTypes} duration={uniqueDurations} />
        </div>
      </div>
    </div>
  );
};

export default OurTourPackages;
